import { useCallback, useMemo, useState } from 'react'
import { AgentProvider } from '@multiplayer-app/ai-agent-react'
import { detectHost, type OfficeHost } from '@/shared/office/host'
import { clearScanHighlight } from '@/shared/office/word'
import type { ColorMode } from '@/shared/office/theme'
import { buildAgentConfig } from '@/shared/agent/config'
import { useAuth } from '@/shared/auth/useAuth'
import { useAiFeatureGate } from '@/shared/hooks/useAiFeatureGate'
import { useColorMode } from '@/shared/hooks/useColorMode'
import { useOfficePageContext } from '@/shared/hooks/useOfficePageContext'
import { ShellProvider, type ShellContextValue } from '@/shared/context/ShellContext'
import { PaneTopBar } from '@/shared/components/PaneTopBar'
import { LoadingScreen } from '@/shared/components/LoadingScreen'
import { LoginScreen } from '@/screens/login/LoginScreen'
import { AiDisabledScreen } from '@/screens/login/AiDisabledScreen'
import { ChatScreen } from '@/screens/chat/ChatScreen'
import { ChecklistScanPane } from '@/screens/scan/ChecklistScanPane'
import { officeToolRenderers } from '@/screens/chat/renderers'

export default function App() {
  const host = useMemo(() => detectHost(), [])
  const { auth, status, error, signingIn, signIn, signInWithSso, signOut } = useAuth()
  const colorMode = useColorMode()

  if (status === 'checking') {
    return <LoadingScreen />
  }

  if (status !== 'authenticated' || !auth) {
    return <LoginScreen onSubmit={signIn} onSso={signInWithSso} error={error} signingIn={signingIn} />
  }

  return <AuthedAgent host={host} token={auth.token} colorMode={colorMode} onSignOut={signOut} />
}

function AuthedAgent({
  host,
  token,
  colorMode,
  onSignOut
}: {
  host: OfficeHost
  token: string
  colorMode: ColorMode
  onSignOut: () => void
}) {

  const { status: featureStatus, checklistEnabled } = useAiFeatureGate(token, onSignOut)

  const config = useMemo(
    () => buildAgentConfig(host, token, { onUnauthorized: onSignOut, colorMode, toolRenderers: officeToolRenderers }),
    [host, token, onSignOut, colorMode]
  )

  if (featureStatus === 'checking') {
    return <LoadingScreen message='Preparing your workspace…' />
  }
  if (featureStatus === 'disabled') {
    return <AiDisabledScreen onSignOut={onSignOut} />
  }

  return (
    <AgentProvider key={token} config={config}>
      <AppContent host={host} checklistEnabled={checklistEnabled} onSignOut={onSignOut} />
    </AgentProvider>
  )
}

/** Pane views: the AI chat, or the checklist-item document scanner (Word only). */
type PaneView = 'chat' | 'scan'

/**
 * Authenticated shell: toggles between the chat and scan screens, with the
 * top-right action overlay. The AgentProvider lives above, so the chat keeps
 * its state across toggles; the scanner stays mounted (just hidden) so its
 * history, scroll and expansion state persist too — and it refreshes silently
 * whenever it becomes visible again.
 */
function AppContent({
  host,
  checklistEnabled,
  onSignOut
}: {
  host: OfficeHost
  checklistEnabled: boolean
  onSignOut: () => void
}) {
  const syncState = useOfficePageContext()
  const [view, setView] = useState<PaneView>('chat')

  // Document scanning + in-document highlighting are Word-specific, and the
  // account must have the aiChecklist feature enabled.
  const canScan = host === 'word' && checklistEnabled

  const toggleView = useCallback(() => {
    setView((current) => {
      const next = current === 'scan' ? 'chat' : 'scan'
      // Leaving the scanner clears any highlight it left in the document.
      if (next === 'chat') void clearScanHighlight()
      return next
    })
  }, [])

  const showScan = canScan && view === 'scan'

  const shell = useMemo<ShellContextValue>(
    () => ({ syncState, showScan, canScan, toggleView, signOut: onSignOut }),
    [syncState, showScan, canScan, toggleView, onSignOut]
  )

  return (
    <ShellProvider value={shell}>
      <div className='ramp-pane'>
        {/* Keep the scanner mounted so its state survives toggles; hide it
            instead of unmounting. Chat unmounts as before — its state lives in
            the AgentProvider above. */}
        {canScan ? (
          <div hidden={!showScan}>
            <ChecklistScanPane />
          </div>
        ) : null}
        {!showScan ? <ChatScreen /> : null}
        <PaneTopBar />
      </div>
    </ShellProvider>
  )
}
